<template>
    <ai-popup
        :title="state.userRequestBody.fio"
        :title-image="userStatusImgSrc"
        :is-explicit-close="false"
        :bottom-buttons-enabled="true"
        :refresh-button-enabled="true"
        :first-button-disabled="isModificationCardDisabled || state.dataHasNotChanged"
        :top-buttons="popupToolbarItems"
        width="60%"
        height="720px"
        v-model:visible="state.userCardPopupVisible"
        @refresh-click="refreshData"
        @submit-click="preparingToSave"
        @close-popup="onHiding"
    >
        <div class="flex h-full flex-col gap-3">
            <dx-load-indicator v-if="loading" width="24px" height="24px" :visible="true" />
            <dx-tabs v-model:selectedIndex="state.currentTabIndex" class="custom-user-tabs">
                <dx-item v-for="(item, index) in tabs" :key="index" :html="item.text" />
            </dx-tabs>
            <users-blockages ref="locksGridRef" v-show="state.currentTabIndex === CARD_TABS.BLOCKAGES" />
            <component :is="tabsSwitcher" />
        </div>
    </ai-popup>
</template>

<script setup>
import { onMounted, ref, computed, watch } from 'vue';
import DxTabs, { DxItem } from 'devextreme-vue/tabs';
import DxLoadIndicator from 'devextreme-vue/load-indicator';

import AiPopup from '@/components/ai-popup/ai-popup.vue';
import UsersCard from '@/views/administration/users/users-card.vue';
import UsersBlockages from '@/views/administration/users/users-blockages.vue';
import UsersRole from '@/views/administration/users/users-role.vue';
import UsersRestriction from '@/views/administration/users/users-restriction.vue';

import usersStore from '@/store/administration/users.js';
import usersLocalStore from '@/views/administration/users/store.js';
import environmentStore from '@/store/enviroment-data.js';
import rolesStore from '@/store/administration/roles.js';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog.js';
import { getURL } from '@/service/http.js';
import { dxUiNotify } from '@/utils/dx-helper.js';
import { getLockReasons } from '@/service/admin-service.js';
import { showLoadPanel, hideLoadPanel } from '@/components/utils/ai-global-load-panel.js';

import activeUserImgSrc from '../../../../public/assets/active-user.svg';
import lockedUserImgSrc from '../../../../public/assets/locked-user.svg';
import deletedUserImgSrc from '../../../../public/assets/deleted-user.svg';

const { usersState, fetchUserData, fetchOfficialUserData, updateUser, createUserData } = usersStore;
const { state, MODE, CARD_TABS, UI_ELEMENTS_IDS, getUserTypesList } = usersLocalStore;
const { state: environmentState, disableUiElement } = environmentStore;
const { rolesState } = rolesStore;

const emit = defineEmits(['save-click', 'refreshUserGrid']);
const locksGridRef = ref();
const refreshUserGrid = ref(false);
const isModificationCardDisabled = disableUiElement(UI_ELEMENTS_IDS.CARD);
let originalUserRequestBody;


onMounted(async () => {
    state.lockTypes = await getLockReasons();
    await getUserTypesList();
    state.isClosingConfirmed = state.dataHasNotChanged;
    if (!state.stopWatching) startWatching();
});

const resetWatchingState = () => {
    if (state.stopWatching) state.stopWatching();
    state.stopWatching = null;
    state.dataHasNotChanged = true;
    state.requestBodyHasNotChanged = true;
    state.userBlockagesHasNotChanged = true;
    state.userRequestBody.deleted_locks = [];
    state.editingChanges = [];
};

const startWatching = () => {
    originalUserRequestBody = structuredClone(state.userRequestBody);
    state.stopWatching = watch(
        [() => locksGridRef.value?.gridInstance?.option('editing.changes'), state.userRequestBody],
        () => {
            state.requestBodyHasNotChanged = JSON.stringify(originalUserRequestBody) === JSON.stringify(state.userRequestBody);
            if (locksGridRef.value?.gridInstance) {
                let changes = locksGridRef.value?.gridInstance?.option('editing.changes');
                changes = changes.filter(change => !change.data.undefined);
                state.editingChanges = changes;
                state.userBlockagesHasNotChanged = changes.length === 0;
            }

            state.dataHasNotChanged = state.userBlockagesHasNotChanged && state.requestBodyHasNotChanged;
            state.isClosingConfirmed = false;
        },
        { deep: true },
    );
};

const tabsSwitcher = computed(() => {
    switch (state.currentTabIndex) {
        case CARD_TABS.INFORMATION:
            return UsersCard;
        case CARD_TABS.ROLES:
            return UsersRole;
        case CARD_TABS.RESTRICTION:
            return UsersRestriction;
        default:
            return null;
    }
});

const userStatusImgSrc = computed(() => {
    switch (state.userRequestBody.status) {
        case 'A':
            return activeUserImgSrc;
        case 'L':
        case 'E':
            return lockedUserImgSrc;
        case 'D':
            return deletedUserImgSrc;
    }
    return '';
});

const popupToolbarItems = [
    {
        svg: 'ai-refresh-in-white-circle',
        click: () => refreshData(),
    },
];

const getMatchingApp = selectedItem => {
    const areasId = selectedItem?.areas?.map(item => item.app_id);
    const matchingApp = environmentState.appList
        .filter(app => areasId && areasId.includes(app.kod_app))
        .map(app => app.name_app)
        .join(', ');
    return matchingApp ? matchingApp : '';
};

const userDataRoles = userData => {
    const filteredRoles = state.userRequestBody.roles?.filter(role => !role.deleted);
    const rolesIds = filteredRoles ? filteredRoles.map(role => role.id) : [];
    return (userData.roles = rolesIds);
};

const computeUpdateUserData = async () => {
    const userData = JSON.parse(JSON.stringify(state.userRequestBody));
    const stringLockReason = state.userRequestBody.lock_reason[0]?.name
        ? state.userRequestBody.lock_reason[0].name
        : state.userRequestBody.lock_reason[0];
    userData.lock_reason = stringLockReason;
    userData.official_id = parseInt(userData.official_id);
    if (!userData) return;

    userDataRoles(userData);
    return userData;
};

const computeCreateUserData = async () => {
    const userData = JSON.parse(JSON.stringify(state.userRequestBody));
    userData.locks = userData.locks?.map(lock => {
        if (lock.id < 1) {
            lock.id = null;
        }
        return { ...lock, user_id: userData.id };
    });
    userData.official_id = parseInt(userData.official_id);
    if (!userData) return;

    userDataRoles(userData);
    userData.origin_url = getURL('');
    return userData;
};

const preparingToSave = async () => {
    showLoadPanel();
    state.isClosingConfirmed = true;
    await onSaveHandler();
};

const onSaveHandler = async () => {
    try {
        const isDateBegLessDateEnd = locksGridRef.value.checkDateBegLessDateEnd();
        if (!isDateBegLessDateEnd) {
            dxUiNotify('Дата начала блокировки должна быть позже даты окончания', 'error');
            return;
        }
        const locks = locksGridRef.value.prepareLocksForSave();
        const oldLocksState = Array.from(state.userRequestBody.locks);
        state.userRequestBody.locks = Array.from(locks);

        const formData = state.userRequestBody;
        const isValid = formData.user_login &&
            formData.request_number &&
            formData.request_date &&
            ((!formData.notification_email && !formData.notification_push) ||
                (formData.notification_days && formData.notification_time));
        if (!isValid) {
            dxUiNotify('Заполните все обязательные поля', 'error');
        } else {
            if (state.mode === MODE.CREATE) {
                const data = await computeCreateUserData();
                const response = await createUserData({ ...data });
                if (response.success) {
                    dxUiNotify('Пользователь создан', 'success');
                    state.mode = MODE.UPDATE;
                    state.userRequestBody.id = response.data.user_id;
                    const event = {
                        row: {
                            key: state.userRequestBody.id,
                        },
                    };
                    refreshUserGrid.value = true;
                    resetWatchingState();
                    await locksGridRef.value.afterSaveInUsersPopup();
                    await locksGridRef.value?.gridInstance?.option('editing.changes', []);
                    emit('save-click', event);
                } else {
                    dxUiNotify(`Ошибка при создании пользователя: ${response.errors[0].message}`, 'error');
                }
            } else {
                const data = await computeUpdateUserData();
                const response = await updateUser({ ...data });
                if (response.success) {
                    dxUiNotify('Данные сохранены', 'success');
                    const event = {
                        row: {
                            key: state.userRequestBody.id,
                        },
                    };
                    refreshUserGrid.value = true;
                    resetWatchingState();
                    await locksGridRef.value.afterSaveInUsersPopup();
                    await locksGridRef.value?.gridInstance?.option('editing.changes', []);
                    emit('save-click', event);
                } else {
                    state.userRequestBody.locks = Array.from(oldLocksState);
                    locksGridRef.value.checkRequiredFieldsValidation();
                    dxUiNotify(`Ошибка при сохранении данных: ${response.errors[0].message}`, 'error');
                }
            }
        }
    } catch (e) {
        state.mode === MODE.CREATE
            ? dxUiNotify('Ошибка при создании пользователя', 'error')
            : dxUiNotify('Ошибка при обновлении данных пользователя', 'error');
    } finally {
        hideLoadPanel();

    }
};

const tabs = [
    { text: 'Персональная информация', component: UsersCard },
    { text: 'Блокировки пользователя', component: UsersBlockages },
    { text: 'Роли пользователя', component: UsersRole },
    { text: 'Ограничения по подразделениям', component: UsersRestriction },
];

const onHiding = async () => {
    if (state.dataHasNotChanged) {
        locksGridRef.value?.gridInstance?.option('editing.changes', []);
        state.userCardPopupVisible = false;
        state.currentTabIndex = CARD_TABS.INFORMATION;
        state.stopWatching = null;
        if (refreshUserGrid.value) emit('refreshUserGrid');
        return;
    }
    if (!state.dataHasNotChanged) {
        const result = await AiCustomConfirmDialog('Cохранить изменения?');
        if (result === -1) {
            return;
        } else if (result === 1) {
            await preparingToSave();
        }
        if (refreshUserGrid.value) emit('refreshUserGrid');
        state.userCardPopupVisible = false;
        state.currentTabIndex = CARD_TABS.INFORMATION;
    }

    resetWatchingState();
    state.currentTabIndex = CARD_TABS.INFORMATION;
};

const refreshData = async () => {
    if (!state.dataHasNotChanged && !isModificationCardDisabled) {
        const result = await AiCustomConfirmDialog('Сохранить изменения?', 'Запрос на сохранение');
        if (result === 1) {
            await preparingToSave();
        } else if (result === -1) {
            return;
        }
    }
    showLoadPanel();
    if (state.stopWatching) state.stopWatching();
    resetWatchingState();
    locksGridRef.value?.gridInstance?.option('editing.changes', []);
    const tab = state.currentTabIndex;
    const cardData = {
        login: '',
        e_mail: '',
        fio: '',
        department: '',
        namedolzh: '',
        phone: '',
        from_ad: false,
        password_expiration: '',
        user_type_id: 1,
        mobile_phone: '',
        request_number: '',
        request_date: '',
        notification_days: null,
        locked_since: null,
        locked_until: null,
        notification_push: false,
        notification_email: false,
        notification_time: null,
    };
    if (state.mode === MODE.CREATE) {
        await fetchOfficialUserData();
        const actualOfficialData = usersState.officialUserData;
        if (tab === 0) {
            Object.assign(cardData, actualOfficialData);
            const newData = Object.assign(state.userRequestBody, cardData);
            state.userRequestBody = newData;
            state.userRequestBody.id = usersState.userId;
            state.userRequestBody.official_id = usersState.userOfficialId;
        }
        if (tab === 1) {
            state.userRequestBody.locks = [];
        }
        if (tab === 2) {
            state.userRequestBody.roles = [];
        }
        if (tab === 3) {
            state.userRequestBody.department_rights = [];
        }
    }
    if (state.mode === MODE.UPDATE) {
        await fetchOfficialUserData();
        await fetchUserData();
        const actualOfficialData = usersState.officialUserData;
        const { roles, department_rights, locks, ...rest } = usersState.userData;

        const initFilteredRoleUser = () => {
            const updatedItems = [];
            if (state.mode !== MODE.CREATE) {
                roles.forEach(roleId => {
                    const matchingItems = rolesState.roleList.filter(item => item.id === roleId);
                    if (matchingItems.length > 0) {
                        const matchedRoles = matchingItems.map(selectedItem => ({
                            ...selectedItem,
                            name_app: getMatchingApp(selectedItem),
                        }));
                        updatedItems.push(...matchedRoles);
                    }
                });
                return updatedItems;
            } else {
                return [];
            }
        };
        state.userRequestBody.locks = locks;
        await locksGridRef.value.setGridDataSource();
        if (tab === 0) {
            Object.assign(cardData, actualOfficialData, rest);
            const newData = Object.assign(state.userRequestBody, cardData);
            state.userRequestBody = newData;
            state.userRequestBody.id = usersState.userId;
            state.userRequestBody.lock_reason = state.userRequestBody.lock_reason
                ? Array.isArray(state.userRequestBody.lock_reason)
                    ? state.userRequestBody.lock_reason.map(reason => ({ id: new Date().getTime(), name: reason }))
                    : [{ id: new Date().getTime(), name: state.userRequestBody.lock_reason }]
                : [];
            state.userRequestBody.official_id = usersState.userOfficialId;
        }
        if (tab === 2) {
            state.userRequestBody.roles = initFilteredRoleUser();
            state.currentTabIndex = 3;
            setTimeout(() => {
                state.currentTabIndex = 2;
            }, 1); //костыль для того, чтобы таб ролей сделал корректный ререндеринг
        }
        if (tab === 3) {
            state.userRequestBody.department_rights = department_rights || [];
        }
    }
    dxUiNotify('Данные обновлены', 'success');
    resetWatchingState();
    startWatching();
    hideLoadPanel();
};

defineExpose({
    startWatching,
});
</script>

<style lang="scss">
.custom-user-tabs {
    .dx-tabs-wrapper {
        display: flex;
        justify-content: space-between;
        gap: 10px;

        .dx-tab {
            border-radius: 8px;
            border: 1px solid #e3e3e3;
            height: 40px;
            text-transform: none !important;

            .dx-item-content > .dx-tab-text {
                font-weight: 400 !important;
            }
        }

        .dx-tab-selected {
            background: #cbe1f7 !important;
            border: none;
            border-top: 3px solid #286eb6 !important;
            border-radius: 8px;

            .dx-item-content > .dx-tab-text {
                font-weight: 500 !important;
            }
        }

        .dx-state-hover {
            border-radius: 8px !important;
            background-color: #e3f1ff !important;
        }

        .dx-state-active {
            border-radius: 8px !important;
            background-color: #e3f1ff !important;
        }

        .dx-inkripple {
            border-radius: 8px !important;
        }

        .dx-inkripple-wave {
            border-radius: 8px !important;
            background-color: #286eb664 !important;
        }
    }

    .dx-item-content .dx-tab-text {
        font-size: 13px !important;
        font-weight: 400 !important;
    }

    .dx-tab .dx-tab-text {
        text-transform: none !important;
    }

    .dx-tab-selected::after {
        background-color: transparent !important;
    }

    .dx-item-content {
        .dx-tab-text {
            font-weight: 500;
            font-size: 10px;
            color: black;
        }
    }
}

.custom-user-popup-buttons {
    .dx-button-text {
        text-transform: none !important;
    }

    .save-button.dx-button {
        width: 214px;
        height: 40px;
        background-color: #286eb6;
        color: white;
        border-radius: 8px;
    }

    .save-button.dx-button.dx-state-hover {
        background-color: #0b4c8c;
    }

    .cancel-button.dx-button {
        width: 214px;
        height: 40px;
        background-color: #cbe1f7;
        color: #031425;
    }

    .cancel-button.dx-button.dx-state-hover {
        background-color: #b6cade;
    }
}
</style>
